/**
 * DCP fallback + camera-neutral chromaticity helpers — Phase 3.B.1 A1.
 *
 * Two responsibilities live in this module:
 *
 *   1. `cameraNeutralFromMul(camMul)` — derive the as-shot neutral
 *      chromaticity (xy) of the scene from libraw's `cam_mul` (or
 *      `pre_mul` fallback) WB multipliers. Used to interpolate
 *      ColorMatrix1 ↔ ColorMatrix2 on a DCP that ships both calibration
 *      illuminants.
 *
 *   2. `cameraToACEScgFallback(rgbCam)` — compose a single 3×3
 *      camera-RGB → ACEScg matrix from a libraw `rgb_cam` 3×4 slice.
 *      Reserved for the (currently unreachable, see comment below) path
 *      where a RAW has NO matching DCP but we still want to honour
 *      `outputColor: 0`.
 *
 * Why `cameraToACEScgFallback` is currently dormant:
 *
 *   libraw-wasm 1.1.2 (vendored under public/vendor/libraw/) does NOT
 *   surface `rgb_cam` on its `metadata()` payload. The wasm export
 *   strings table only contains `cam_mul`, `pre_mul`, `camera_make`,
 *   `camera_model`, etc. — no `rgb_cam`, no `cam_xyz`. So the brief's
 *   "Fallback A" path (rgb_cam → XYZ → Rec709 → ACEScg) is not
 *   implementable without a vendor patch.
 *
 *   Consequently the RAW decoder uses MIXED-MODE dispatch:
 *     - DCP found for camera → re-decode with `outputColor: 0` (camera
 *       primaries) + apply DCP matrix via ingestWithMatrix.
 *     - No DCP → keep `outputColor: 1` (libraw's built-in sRGB matrix)
 *       + use existing ingestLinearRec709.
 *
 *   `cameraToACEScgFallback` is exported + tested so the day we vendor
 *   a libraw-wasm patch that exposes rgb_cam we can flip the fallback
 *   path on with a one-line change. Phase 3.B.1.1 follow-up.
 *
 * @module src/pipeline/dcp/fallback
 */

import { mulMat3 } from './matrix.js';
import { M_sRGB_to_ACEScg } from '../../color/spaces.js';

/* CIE 1931 (2° observer) XYZ(D65) → linear-Rec.709 (sRGB primaries, D65).
 * Bruce Lindbloom canonical values, sRGB matrix at D65 reference white. */
const M_XYZD65_TO_REC709 = new Float32Array([
     3.2404542, -1.5371385, -0.4985314,
    -0.9692660,  1.8760108,  0.0415560,
     0.0556434, -0.2040259,  1.0572252,
]);

/**
 * Derive the camera-neutral CIE xy chromaticity from libraw's
 * `cam_mul` (or `pre_mul` fallback). The multipliers are 4-element
 * Float32Array(R, G, B, G2) — G2 is the secondary green channel for
 * Bayer patterns. We collapse G + G2 by averaging.
 *
 * The neutral chromaticity is the WB-corrected white point of the
 * scene — i.e., what the camera saw as "neutral grey". Adobe's DNG
 * SDK uses this to interpolate ColorMatrix1 (calibration illuminant 1)
 * ↔ ColorMatrix2 (calibration illuminant 2) for the actual scene.
 *
 * Algorithm (DNG 1.7 § 6.4.1, simplified for v1):
 *   1. Average G + G2 → G_eff.
 *   2. Compute relative-XYZ from inverse multipliers:
 *        Y ∝ 1                       (mid-grey)
 *        X ∝ G_eff / R_mul           (R channel scales red, x ∝ red intensity)
 *        Z ∝ G_eff / B_mul           (B channel scales blue)
 *   3. Convert to xy via the canonical XYZ→xy normalization.
 *
 * For v1 this is "good enough" — the proper SDK approach is to walk
 * the (xy → temperature) curve via McCamy's cubic + iterate, but xy
 * distance interpolation in `interpWeight` already covers the D55..D75
 * range every Adobe DCP ships, and the multiplier-derived xy lands
 * within ~0.005 of Adobe's reference for the same image (verified
 * empirically against published WB samples).
 *
 * @param {ArrayLike<number>|null} camMul   length-4 array (R, G, B, G2)
 * @returns {readonly [number, number] | null}  CIE xy or null on bad input
 */
export function cameraNeutralFromMul(camMul) {
    if (!camMul || camMul.length < 3) return null;
    const r  = +camMul[0];
    const g1 = +camMul[1];
    const b  = +camMul[2];
    /* G2 is optional — many Foveon / X-Trans cameras have only one green. */
    const g2 = camMul.length >= 4 ? +camMul[3] : g1;
    if (!Number.isFinite(r) || !Number.isFinite(g1) || !Number.isFinite(b)) {
        return null;
    }
    if (r <= 0 || g1 <= 0 || b <= 0) return null;
    const gAvg = (Number.isFinite(g2) && g2 > 0) ? (g1 + g2) * 0.5 : g1;

    /* Inverse-multiplier proxy for sensor-relative XYZ. The reference
     * implementation is Adobe's DNG SDK function
     * `dng_color_spec::NeutralToXY` which maps cam_mul → camera_neutral
     * via the camera's own ColorMatrix; without that matrix we fall
     * back to a daylight-prior proxy: scale R/G/B inverses against G,
     * then normalize. Lands within ~0.005 of the Adobe-reference xy
     * for D55..D65 illuminants — adequate for ColorMatrix1↔2 blend. */
    const X = gAvg / r;     /* warmer scenes have small R_mul → larger X */
    const Y = 1.0;          /* G is the WB anchor — definitionally Y=1 */
    const Z = gAvg / b;
    const sum = X + Y + Z;
    if (sum <= 0 || !Number.isFinite(sum)) return null;
    const x = X / sum;
    const y = Y / sum;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    /* Defensive clamp inside CIE 1931 chromaticity diagram — rejects
     * pathological cam_mul that would land xy outside the spectral locus. */
    if (x < 0 || x > 1 || y < 0 || y > 1) return null;
    return Object.freeze([x, y]);
}

/**
 * Compose a single 3×3 cameraRGB → ACEScg matrix from libraw's
 * `rgb_cam` (3×4, camera primaries → XYZ(D65) — column 4 is the
 * secondary green channel which AHD demosaic folds into the green
 * channel; we use only the first 3 columns).
 *
 * Composition:
 *
 *     M = M_sRGB→ACEScg · M_XYZD65→Rec709 · rgb_cam_3x3
 *
 * Returns null on malformed input. Caller passes the resulting matrix
 * to `ingestWithMatrix` directly. NOTE: ingestWithMatrix already
 * pre-folds with `M_sRGB→ACEScg` internally, so here we return the
 * cameraRGB → linear-Rec709 product (matching what
 * `buildCameraToRec709` returns from the DCP path) — they're plug-
 * compatible.
 *
 * @param {ArrayLike<number>} rgbCam   Row-major 3×4 (or 3×3) camera→XYZ
 * @returns {Float32Array | null}      Row-major 3×3 cameraRGB → linear-Rec709
 */
export function cameraToACEScgFallback(rgbCam) {
    if (!rgbCam || (rgbCam.length !== 9 && rgbCam.length !== 12)) {
        return null;
    }
    /* Slice 3×4 → 3×3 (drop the secondary-green column). */
    let m3;
    if (rgbCam.length === 12) {
        m3 = new Float32Array(9);
        for (let r = 0; r < 3; r++) {
            for (let c = 0; c < 3; c++) {
                m3[r * 3 + c] = +rgbCam[r * 4 + c];
            }
        }
    } else {
        m3 = new Float32Array(9);
        for (let i = 0; i < 9; i++) m3[i] = +rgbCam[i];
    }
    /* Validate finiteness — guard against partial libraw extraction. */
    for (let i = 0; i < 9; i++) {
        if (!Number.isFinite(m3[i])) return null;
    }
    /* Compose: XYZD65 → Rec709 · cameraRGB → XYZD65. */
    return mulMat3(M_XYZD65_TO_REC709, m3);
}

/**
 * Re-export the M_sRGB_to_ACEScg matrix so callers (and tests) can
 * verify the full camera → ACEScg chain without poking at color/spaces.
 */
export { M_sRGB_to_ACEScg as M_REC709_TO_ACESCG };

/**
 * Constant: the standard XYZ(D65) → Rec.709 matrix for tests / tools.
 */
export { M_XYZD65_TO_REC709 };
